import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import type { LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { getMarket } from '@clearscore-group/lib.helpers.market';
import { getPersonCookie, getCookie, constants } from '@clearscore-group/lib.helpers.cookies';
import type { Market } from '@clearscore-group/lib.constants.market';

export type AppKey =
    | 'webapp'
    | 'drivescoreWebapp'
    | 'drivescoreWebsite'
    | 'clearscoreWebsite'
    | 'group-website'
    | 'clearer-webapp'
    | 'clearscore-webapp';

export const ddApps = {
    webapp: 'clearscore-webapp',
    drivescoreWebapp: 'drivescore-webapp',
    drivescoreWebsite: 'drivescore-website',
    clearscoreWebsite: 'clearscore-website',
    'group-website': 'group-website',
    'clearer-webapp': 'clearer-webapp',
    'clearscore-webapp': 'clearscore-webapp',
} as const satisfies Record<AppKey, string>;

export type DDAppKey = keyof typeof ddApps;

export const ddApplicationIds: Record<DDAppKey, string> = {
    webapp: '3eb18c69-38e0-4095-80e2-78aab47831ae',
    drivescoreWebapp: '4f347bd3-31d2-41c0-a304-4b542321bfaa',
    drivescoreWebsite: '97a55d2a-3577-4acd-8cde-b7b4a2eb6f8c',
    clearscoreWebsite: '637b8cb2-1223-40ce-bf0a-7f6dbea7a188',
    'group-website': '6031bc1e-2a87-4fbc-8325-35107239f390',
    'clearer-webapp': '4bdbfc94-01e0-44fb-aafb-30df4819cd90',
    'clearscore-webapp': '824de53d-84e2-4638-b470-359fde62b837',
} as const;

export const ddClientTokens: Record<DDAppKey, string> = {
    webapp: 'pub29a684674f4826c6737542cce2219201',
    drivescoreWebapp: 'pub8b95eb1a276fc5e5d1e4d5a928d49b2b',
    drivescoreWebsite: 'pubf94cd5795b86d1abd74dbbaae949ac7b',
    clearscoreWebsite: 'pubce82de9f9e4cbf8ee2f2a8ba0db399bc',
    'group-website': 'pubf4b21d99bf416a68ede721806887dea0',
    'clearer-webapp': 'pub48a60a8ecfa645614793c5504ad55a4b',
    'clearscore-webapp': 'puba9e6920f57b4d34db39de58249cbb106',
} as const;

export type DDAppValue = (typeof ddApps)[DDAppKey];
type DDApplicationIdValue = (typeof ddApplicationIds)[DDAppKey];
type DDClientToken = (typeof ddClientTokens)[DDAppKey];
type DDEnvironment = 'ci' | 'staging' | 'production';
type DDInstance = typeof datadogRum | typeof datadogLogs;

export const DD_DEFAULT_SESSION_SAMPLE_RATE = 0;
export const DD_DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const DD_DEFAULT_TRACE_SAMPLE_RATE = 10;
const DD_SITE = 'datadoghq.eu';
const DD_DEFAULT_PRIVACY_LEVEL = 'mask';

export const DD_ALLOWED_TRACING_URLS = ['https://app-stg.cs55.co.uk/api'];

const environmentMap: Record<string, DDEnvironment> = {
    ci: 'ci',
    stg: 'staging',
    prod: 'production',
} as const;

const uuidFunctionMap: Record<DDAppKey, () => string | undefined> = {
    webapp: getPersonCookie,
    drivescoreWebapp: getPersonCookie,
    drivescoreWebsite: getPersonCookie,
    clearscoreWebsite: getPersonCookie,
    'group-website': getPersonCookie,
    'clearer-webapp': () => undefined,
    'clearscore-webapp': getPersonCookie,
} as const;

const getUuid = (clientToken: string) => {
    const app = Object.keys(ddClientTokens).find((key) => ddClientTokens[key as DDAppKey] === clientToken);
    return uuidFunctionMap[app as DDAppKey]();
};

const setUser = (clientToken: string, dataDogInstance: DDInstance) => {
    let uuid;
    try {
        uuid = getUuid(clientToken);
        if (typeof uuid === 'string') {
            dataDogInstance.setUser({ id: uuid });
        } else {
            throw new Error('Invalid UUID');
        }
    } catch (e) {
        // Don't set uuid.
    }
};

/**
 * SSR apps inject `window.ENV` from the document (see sdui-app root.tsx).
 * We avoid augmenting `Window` in global.d.ts — that collides with
 * `@clearscore-group/lib.sdui-modules.sdui-app-env` (TS2687 identical modifiers).
 */
interface InjectedClientEnv {
    ENV?: string;
    MARKET?: string;
    COMMIT_REF?: string;
}

const isBrowser = () => typeof window !== 'undefined';

function getInjectedEnv(): InjectedClientEnv | undefined {
    if (!isBrowser()) {
        return undefined;
    }
    return (window as Window & { ENV?: InjectedClientEnv }).ENV;
}

function getRelease() {
    if (window.__cs__?.release?.env) {
        // Standard Applications
        return environmentMap[window.__cs__.release.env];
    }

    const injected = getInjectedEnv();
    if (injected?.ENV) {
        // SSR-hydrated clients (window.ENV from inline script)
        return environmentMap[injected.ENV];
    }

    return undefined;
}

function getMappedEnv(market: Market | null) {
    if (!market) {
        return null;
    }
    return `${market}-${getRelease()}`;
}

function calculateMarket() {
    let market = getMarket();

    const injected = getInjectedEnv();
    if (!market && injected?.MARKET) {
        market = injected.MARKET as Market;
    }

    if (market === undefined) {
        // Fallback to the browser locale from the i18next-browser-languagedetector language detector
        // (packages/clearscore-group/libs/redux/cs-provider/src/lib/i18n.ts)
        // as a best guess for market on the login screen.
        const currentLanguage = getCookie(constants.CS_CURRENT_LANGUAGE);

        if (currentLanguage === undefined) {
            return null;
        }

        // We currently only have `en-${market}` languages; if this ever changes, update this.
        [, market] = currentLanguage.split('-');
    }

    return market || null;
}

type ErrorMessage = string;
type Reason = string;

export const errorMessagesToFilter: Record<ErrorMessage, Reason> = {
    'Object Not Found Matching Id:': 'Login page issue',
    ResizeObserver: 'Not polyfilled',
    'Script error.':
        'This is from CORS errors from 3rd party scripts (https://server-side-tagging / Persona) in Safari. TODO: Fix the CORS',
    'Error sending segment performance metrics': 'Segment performance metrics failure (3rd party)',
    "Cannot read properties of null (reading 'reason')": 'Login epic type error isMigrationType',
    'Failed to fetch': 'Segment 3rd party',
    'Failed to load Analytics.js': 'Segment 3rd party',
    'Failed to import: //cdn2-fs.clearscore.com/': 'Flux lazy loading failure',
    'Error css file. src: //cdn2-fs.clearscore.com/': 'Flux lazy loading css failure',
    'Load failed': 'Segment 3rd party',
    'Uncaught "Empty reason"': 'Not sure but across all verticals',
};

export const beforeSend = (event: RumEvent) => {
    if (event.type === 'error' && event.error) {
        const referrerUrl = event.view?.referrer || document.referrer || '';

        if (referrerUrl) {
            let refHost = '';
            try {
                refHost = new URL(referrerUrl).host;
            } catch {
                return false;
            }

            const allowedHosts = new Set([
                'app.clearscore.com',
                'app-stg.cs55.co.uk',
                'app.drivescore.com',
                'ds-stg.cs55.co.uk',
            ]);

            if (!allowedHosts.has(refHost)) {
                return false;
            }
        }

        return !Object.keys(errorMessagesToFilter).some((errorMessage) => event.error.message.includes(errorMessage));
    }

    return true;
};

export const initialiseDataDogRUM = ({
    app,
    applicationId,
    clientToken,
    sessionSampleRate = DD_DEFAULT_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate = DD_DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
    env: envOverride,
}: {
    app: DDAppValue;
    applicationId: DDApplicationIdValue;
    clientToken: DDClientToken;
    sessionSampleRate?: RumInitConfiguration['traceSampleRate'];
    sessionReplaySampleRate?: RumInitConfiguration['sessionReplaySampleRate'];
    /** Explicit env string (e.g. 'gb-production'). Bypasses getRelease()/calculateMarket() auto-detection. */
    env?: string;
}) => {
    if (!isBrowser()) {
        return;
    }

    if (!envOverride && !getRelease()) {
        return;
    }

    const market = calculateMarket();
    const mappedEnv = envOverride ?? getMappedEnv(market);

    if (mappedEnv && !window.DD_RUM?.getInternalContext() && !mappedEnv.includes('ci')) {
        datadogRum.init({
            applicationId,
            clientToken,
            site: DD_SITE,
            service: app,
            env: mappedEnv,
            version: window.__cs__?.release?.core ?? getInjectedEnv()?.COMMIT_REF,
            sessionSampleRate,
            sessionReplaySampleRate,
            traceSampleRate: DD_DEFAULT_TRACE_SAMPLE_RATE,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: DD_DEFAULT_PRIVACY_LEVEL,
            beforeSend,
            enablePrivacyForActionName: true,
            startSessionReplayRecordingManually: true,
            trackingConsent: 'granted',
            allowedTracingUrls: DD_ALLOWED_TRACING_URLS,
        });
        setUser(clientToken, datadogRum);
    }
};

export const initialiseDataDogLogs = (
    clientToken: DDClientToken,
    sessionSampleRate: LogsInitConfiguration['sessionSampleRate'] = DD_DEFAULT_SESSION_SAMPLE_RATE,
) => {
    if (!isBrowser()) {
        return;
    }

    if (!getRelease()) {
        return;
    }

    const market = calculateMarket();
    const mappedEnv = getMappedEnv(market);

    if (mappedEnv && !window.DD_LOGS?.getInternalContext() && !mappedEnv.includes('ci')) {
        datadogLogs.init({
            clientToken,
            site: DD_SITE,
            forwardErrorsToLogs: true,
            sessionSampleRate,
            usePciIntake: true,
            env: mappedEnv,
        });
        setUser(clientToken, datadogLogs);
    }
};
